/**
 * Invoice Detail Page
 *
 * Complete detail view for a single invoice record with tabs
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { persistedInvoiceRecords } from '../../services/mockData/invoices';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { useToast } from '../../contexts/ToastContext';
import { FileText, Package, CreditCard, History, Copy, ExternalLink, Receipt } from 'lucide-react';
import { useInvoice, useFinalizeInvoice, useMarkInvoicePaid, useGeneratePaymentLink } from '../../services/queries/invoiceQueries';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { Invoice } from '../../types/index';

interface InvoiceDetailPageProps {
  id: string;
  onBack: () => void;
}

export const InvoiceDetailPage: React.FC<InvoiceDetailPageProps> = ({ id, onBack }) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');

  const { data: invoice, isLoading } = useInvoice(id);
  const isApiSourced = shouldUseGobodo() && typeof id === 'string' && id.startsWith('in_');

  const finalizeInvoice = useFinalizeInvoice();
  const markPaid = useMarkInvoicePaid();
  const generateLink = useGeneratePaymentLink();

  const handleDelete = () => {
    if (!invoice) return;
    if (isApiSourced) {
      error('Cannot delete', 'This invoice is managed in Stripe and cannot be deleted here.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoiceNumber}?`)) return;

    const allInvoices = persistedInvoiceRecords.get();
    persistedInvoiceRecords.set(allInvoices.filter(inv => inv.id !== id));
    success('Invoice deleted', `Invoice ${invoice.invoiceNumber} has been deleted`);
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    onBack();
  };

  const handleFinalizeInvoice = () => {
    if (!invoice) return;
    finalizeInvoice.mutate(id, {
      onSuccess: () => {
        success('Invoice finalized', 'Payment link is now available. Share it with the customer.');
      },
      onError: () => {
        error('Error', 'Failed to finalize invoice');
      }
    });
  };

  const handleMarkPaid = () => {
    if (!invoice) return;
    markPaid.mutate(id, {
      onSuccess: () => {
        success('Invoice marked as paid', `Invoice ${invoice.invoiceNumber} has been marked as paid`);
      },
      onError: () => {
        error('Error', 'Failed to mark invoice as paid');
      }
    });
  };

  const handleGeneratePaymentLink = () => {
    if (!invoice) return;
    generateLink.mutate(id, {
      onSuccess: () => {
        success('Payment link generated', 'Payment link has been generated');
      },
      onError: () => {
        error('Error', 'Failed to generate payment link');
      }
    });
  };

  const handleCopyPaymentLink = () => {
    if (!invoice?.paymentLink) return;
    navigator.clipboard.writeText(invoice.paymentLink);
    success('Copied', 'Payment link copied to clipboard');
  };

  const handleShareWithCustomer = () => {
    if (!invoice?.paymentLink) return;
    navigator.clipboard.writeText(invoice.paymentLink);
    success(
      'Link copied',
      'Payment link copied. Paste it in an email or message to share the invoice with the customer.'
    );
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Invoices">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!invoice) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Invoices">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Invoice not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'lineItems', label: 'Line Items', icon: Package },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'transactions', label: 'Transactions', icon: Receipt },
    { id: 'history', label: 'History', icon: History }
  ];

  // Derive payment transactions for this invoice (paid = one transaction when paidDate set)
  const transactions = invoice.status === 'paid' && invoice.paidDate
    ? [{ id: '1', date: invoice.paidDate, amount: invoice.totalAmount, method: 'Payment', status: 'Completed' as const }]
    : [];

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Invoices">
      <div className="max-w-6xl mx-auto space-y-6">
        <DetailHeader
          title={invoice.invoiceNumber}
          subtitle={`${invoice.clientName} • ${invoice.status}`}
          recordId={invoice.id}
          status={invoice.status}
          statusColor={statusColors[invoice.status] || statusColors.draft}
          onEdit={isApiSourced ? undefined : () => {}}
          onDelete={isApiSourced ? undefined : handleDelete}
          additionalActions={
            <div className="flex items-center gap-2">
              {invoice.status === 'draft' && (
                <button
                  onClick={handleFinalizeInvoice}
                  disabled={finalizeInvoice.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {finalizeInvoice.isPending ? 'Finalizing…' : 'Finalize'}
                </button>
              )}
              {invoice.status === 'sent' && (
                <>
                  {!invoice.paymentLink && (
                    <button
                      onClick={handleGeneratePaymentLink}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Generate Payment Link
                    </button>
                  )}
                  <button
                    onClick={handleMarkPaid}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Mark as Paid
                  </button>
                </>
              )}
            </div>
          }
        />

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {invoice.clientId && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Client</p>
              <Link
                to={`/clients/${invoice.clientId}`}
                className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:underline"
              >
                {invoice.clientName}
              </Link>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                View client profile and transactions
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Subtotal</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(invoice.subtotal)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tax</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(invoice.taxAmount)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(invoice.totalAmount)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Due Date</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Date(invoice.dueDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Issue Date</h3>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(invoice.issueDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Due Date</h3>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                  {invoice.paidDate && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Paid Date</h3>
                      <p className="text-gray-900 dark:text-white">
                        {new Date(invoice.paidDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
                {invoice.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Notes</h3>
                    <p className="text-gray-900 dark:text-white">{invoice.notes}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'lineItems' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Unit Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Discount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {invoice.lineItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{item.productName}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{item.productCode}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(item.unitPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {item.discount ? `${item.discount}%` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(item.lineTotal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            No payment transactions yet. When this invoice is marked as paid, the payment will appear here.
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {new Date(tx.date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(tx.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{tx.method}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {tx.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'payment' && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-[#334155] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Status</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[invoice.status]}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </div>
                    {invoice.paidDate && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Paid Date</span>
                        <span className="text-gray-900 dark:text-white">
                          {new Date(invoice.paidDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {invoice.paymentLink ? (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Payment Link</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Share this link with the customer so they can view and pay the invoice. Copy it and paste into an email or message.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={invoice.paymentLink}
                        readOnly
                        className="flex-1 min-w-[200px] px-4 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={handleShareWithCustomer}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Share with customer
                      </button>
                      <button
                        onClick={handleCopyPaymentLink}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </button>
                      <a
                        href={invoice.paymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-[#334155] rounded-lg p-6 text-center">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">No payment link generated</p>
                    <button
                      onClick={handleGeneratePaymentLink}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors mx-auto"
                    >
                      Generate Payment Link
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <ActivityTab
                recordId={invoice.id}
                recordType={undefined}
                onLogActivity={() => {}}
              />
            )}
          </div>
        </div>
      </div>
    </DetailPageLayout>
  );
};
